/**
 * Move a region to a different parent, or back to top level (BON-448).
 *
 * Restructuring changes what national reporting means, so nothing happens on
 * picking a destination — the operator sees what would move, and confirms. This
 * replaces the drag-to-reparent removed in BON-423, where a single slip
 * reparented a region with no confirmation and no way back.
 *
 * Refusals come from the server, which mirrors the BON-443 database triggers, so
 * the reason shown here is the same reason the database would give.
 */
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';

import type { MovePreview } from '../../../../api/regionMove';
import type { RegionDetail, RegionNode } from '../../../../api/regions';

import { flattenById } from './regionRoot';
import { useMoveRegion, useRegionMovePreview } from './regionsQueries';

const TOP_LEVEL = '__top__';

const selectStyle: CSSProperties = {
  height: 28,
  borderRadius: 7,
  border: '1px solid var(--nb-border)',
  background: 'var(--nb-bg)',
  font: 'inherit',
  fontSize: 12,
  color: 'var(--nb-ink)',
};

function Line({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 11.5, color: 'var(--nb-subtle)' }}>{children}</div>;
}

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** What travels with the region. Grants only appear when there are any — they
 *  are usually zero, and a "0 staff assignments" clause reads like a warning. */
function movesWithIt(preview: {
  venuesMoving: number;
  subregionsMoving: number;
  grantsAffected: number;
}) {
  const parts = [
    plural(preview.venuesMoving, 'venue'),
    plural(preview.subregionsMoving, 'subregion'),
  ];
  if (preview.grantsAffected > 0) parts.push(plural(preview.grantsAffected, 'staff assignment'));
  return parts.join(' · ');
}

/** What the move would do, or why it is refused. */
function PreviewPanel({ preview, loading }: { preview?: MovePreview; loading: boolean }) {
  if (loading) return <Line>Checking what would move…</Line>;
  if (!preview) return null;

  if (preview.blockers.length > 0) {
    return (
      <div style={{ display: 'grid', gap: 2 }}>
        {preview.blockers.map((b) => (
          <div key={b.kind} style={{ fontSize: 11.5, color: 'var(--nb-risk)', fontWeight: 600 }}>
            {b.message}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <Line>Moves with it: {movesWithIt(preview)}.</Line>
      {preview.sourceRootName !== null || preview.destinationRootName !== null ? (
        <Line>
          Reported under {preview.sourceRootName ?? 'its own top-level row'} today,{' '}
          {preview.destinationRootName ?? 'its own top-level row'} afterwards.
        </Line>
      ) : null}
    </div>
  );
}

/** A refusal from the server — it mirrors the database triggers, so this is the
 *  same reason the write itself would give. */
function MoveError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div style={{ fontSize: 11.5, color: 'var(--nb-risk)', fontWeight: 600 }}>
      {error instanceof Error ? error.message : 'That move was refused.'}
    </div>
  );
}

/** Names for the two ends of a completed move, for the undo strip. */
function describeMove(
  undoParentId: string | null,
  landedParentId: string | null,
  regions: RegionNode[],
): Moved {
  const byId = flattenById(regions);
  return {
    undoParentId,
    undoName: undoParentId ? (byId.get(undoParentId)?.name ?? 'its previous parent') : 'top level',
    landedName: landedParentId
      ? (byId.get(landedParentId)?.name ?? 'the chosen region')
      : 'top level',
  };
}

/** Where to move it. Excludes the region itself — the server refuses that
 *  anyway, but offering it invites the mistake. "No parent" is a real choice,
 *  distinct from having chosen nothing yet. */
function DestinationPicker({
  value,
  candidates,
  onChange,
}: {
  value: string;
  candidates: Array<{ id: string; name: string }>;
  onChange: (next: string) => void;
}) {
  return (
    <select
      aria-label="New parent region"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={selectStyle}
    >
      <option value="">Choose parent…</option>
      <option value={TOP_LEVEL}>No parent (top level)</option>
      {candidates.map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}
        </option>
      ))}
    </select>
  );
}

interface Moved {
  undoParentId: string | null;
  undoName: string;
  landedName: string;
}

/** Confirmation of what just happened, with the reverse move one click away.
 *  Undo is an ordinary move (design T5), so it is audited like any other and
 *  survives a missed click — the control above can always move it back. */
function UndoStrip({
  moved,
  pending,
  onUndo,
}: {
  moved: Moved | null;
  pending: boolean;
  onUndo: () => void;
}) {
  if (!moved) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5 }}>
      <span style={{ color: 'var(--nb-subtle)' }}>
        Moved to {moved.landedName}. Undo sends it back to {moved.undoName}.
      </span>
      <button
        type="button"
        className="nb-btn"
        style={{ height: 22, fontSize: 11 }}
        disabled={pending}
        onClick={onUndo}
      >
        Undo
      </button>
    </div>
  );
}

/** The row's state machine: what is chosen, what the server says about it, and
 *  what just happened. Kept apart from the markup so each stays readable. */
function useMoveRowState(detail: RegionDetail, regions: RegionNode[]) {
  // `null` is a real destination (top level), so "untouched" is its own state.
  const [choice, setChoice] = useState<string>('');
  const [moved, setMoved] = useState<Moved | null>(null);
  const move = useMoveRegion();

  const chosen = choice === '' || choice === TOP_LEVEL ? null : choice;
  const preview = useRegionMovePreview(detail.id, chosen, choice !== '');
  // A region cannot move under itself; the server refuses it either way, but
  // offering it invites the mistake.
  const candidates = [...flattenById(regions).values()].filter((r) => r.id !== detail.id);
  const blocked = (preview.data?.blockers.length ?? 0) > 0;

  return {
    choice,
    chosen,
    moved,
    move,
    preview,
    candidates,
    ready: choice !== '' && !preview.isFetching && !blocked && !move.isPending,
    pick: (next: string) => {
      setChoice(next);
      setMoved(null);
      move.reset();
    },
    confirm: () =>
      move.mutate(
        { id: detail.id, parentId: chosen },
        {
          onSuccess: () => {
            setMoved(describeMove(detail.parentId ?? null, chosen, regions));
            setChoice('');
          },
        },
      ),
    undo: () =>
      moved &&
      move.mutate(
        { id: detail.id, parentId: moved.undoParentId },
        { onSuccess: () => setMoved(null) },
      ),
  };
}

export function RegionMoveRow({
  detail,
  regions,
  divider,
}: {
  detail: RegionDetail;
  regions: RegionNode[];
  divider?: boolean;
}) {
  const { choice, moved, move, preview, candidates, ready, pick, confirm, undo } = useMoveRowState(
    detail,
    regions,
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 0',
        borderBottom: divider ? '1px solid var(--nb-border)' : undefined,
      }}
    >
      <div style={{ flex: 1, display: 'grid', gap: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nb-ink)' }}>
          Move to a different parent
        </div>
        <Line>
          Changes which top-level region reports this one and everything inside it. Every move is
          recorded and can be moved back.
        </Line>
        {choice !== '' ? (
          <PreviewPanel preview={preview.data} loading={preview.isFetching} />
        ) : null}
        <MoveError error={move.isError ? move.error : null} />
        <UndoStrip moved={moved} pending={move.isPending} onUndo={undo} />
      </div>
      <DestinationPicker value={choice} candidates={candidates} onChange={pick} />
      <button
        type="button"
        className="nb-btn"
        style={{ height: 28, fontSize: 12 }}
        disabled={!ready}
        onClick={confirm}
      >
        {move.isPending ? 'Moving…' : 'Move…'}
      </button>
    </div>
  );
}
